import { MagnifyingGlassIcon, CaretUpIcon, CaretDownIcon, UploadSimpleIcon, FadersIcon, Spinner, GearIcon, FilePdf, Lock } from '@phosphor-icons/react';
import { useState } from 'react';
import { useAlumni } from '../hooks/useAlumni';
import { alumniService, type AlumniProfile } from '../../../services/alumni.service';
import { useHaptic } from '../../../hooks/useHaptic';
import { useScrollRestoration } from '../../../hooks/useScrollRestoration';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableRowSkeleton } from '../../../components/ui/Skeleton';
import { Button } from '../../../components/ui/Button';
import { ErrorBoundary } from '../../../components/ui/ErrorBoundary';
import { ImportDataModal } from './ImportDataModal';
import { AddDataModal } from './AddDataModal';
import { ScraperSettingsModal } from './ScraperSettingsModal';
import { ScrapeProgressModal } from './ScrapeProgressModal';
import { ResearchChangesTable } from './ResearchChangesTable';

import { PageHeader } from '../../../components/ui/PageHeader';
import { Pagination } from '../../../components/ui/Pagination';
import { formatToCr, parseToNumber } from '../../../utils/currencyFormatter';
import { motion, AnimatePresence } from 'framer-motion';
import { linkedinService, type ScrapeEventMessage } from '../../../services/linkedin.service';
import posthog from '../../../posthog';

const BUSINESS_SECTORS = [
  "MANUFACTURING/HEAVY MACHINERY/METALLURGY",
  "FMCG, CONSUMER GOODS AND RETAIL",
  "FINANCE, BANKING AND VENTURE CAPITAL",
  "ENTERTAINMENT, MEDIA & SPORTS",
  "TECHNOLOGY, HEALTHCARE",
  "STRATEGY CONSULTING & CORPORATE ADVISORY",
  "SOFTWARE, SAAS & CONSUMER TECH PLATFORM",
  "PUBLIC POLICY, GOVERNANCE & SOCIAL IMPACT",
  "INFRASTRUCTURE, CIVIL ENGG",
  "SEMICONDUCTORS, ELECTRONICS & ELECTRICAL EQUIPMENT",
  "AUTOMOTIVE & MOBILITY",
  "ENERGY, OIL & GAS & RENEWABLE ENERGY",
  "AEROSPACE, DEFENCE & SPACE",
  "PHARMA, BIOTECH & MEDICAL DEVICES",
  "CHEMICALS & SPECIALTY MATERIALS",
  "CLEAN TECH, CLIMATE & ENVIRONMENT",
  "EDUCATION & EDTECH",
  "AGRICULTURE, AGRITECH & FOOD PROCESSING",
  "ARCHITECTURE, DESIGN & CREATIVE SERVICES",
  "NON-PROFIT, PHILANTHROPY & DEVELOPMENT ORGANIZATIONS",
  "OTHER"
];

/** Short labels for last_modified_source (see the backend's app/utils/modified.py). */
const MODIFIED_SOURCE_LABELS: Record<string, string> = {
  import: 'Import',
  donations: 'Donations',
  added: 'Added',
  linkedin_edit: 'LinkedIn edit',
  linkedin_scrape: 'LinkedIn scrape',
  vals: 'VALS',
  ai_research: 'AI research',
};

/** Safely convert any MongoDB-serialized value to a renderable string. */
const safeStr = (val: unknown, fallback = 'N/A'): string => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') {
    const keys = Object.keys(val as object);
    if (keys.length === 1 && keys[0].startsWith('$')) return String(Object.values(val as object)[0]);
    return JSON.stringify(val);
  }
  const s = String(val).trim();
  return s === '' ? fallback : s;
};

const formatDirectoryToCr = (askStr: string | null | undefined): string => {
  if (!askStr) return 'N/A';

  const lowerStr = askStr.toLowerCase().trim();
  if (lowerStr === 'not available' || lowerStr === 'not found' || lowerStr === 'n/a') {
    return askStr;
  }

  const parts = askStr.split(/\s*(?:-|\u2013|to)\s*/i);
  const min = parseToNumber(parts[0]);
  const max = parts.length > 1 ? parseToNumber(parts[1]) : null;

  const formatNumber = (val: number): string => {
    if (val < 100000) return val.toLocaleString('en-IN');
    return `${parseFloat((val / 10000000).toFixed(4))} Cr`;
  };

  if (min !== null && max !== null) return `${formatNumber(min)} - ${formatNumber(max)}`;
  if (min !== null) return formatNumber(min);

  return askStr;
};

interface AlumniDirectoryViewProps {
  onBack: () => void;
}

export function AlumniDirectoryView({ onBack }: AlumniDirectoryViewProps) {
  const { lightTap, mediumTap } = useHaptic();
  const limit = 50;

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isAddDataModalOpen, setIsAddDataModalOpen] = useState(false);
  const [isScraperSettingsOpen, setIsScraperSettingsOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // ── Admin LinkedIn bulk scrape state ──────────────────────────────────────
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [isScrapingMissing, setIsScrapingMissing] = useState(false);
  const [isScrapingAll, setIsScrapingAll] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<{ text: string; ok: boolean } | null>(null);

  // ── Scrape Progress State ─────────────────────────────────────────────────
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [progressTitle, setProgressTitle] = useState('');
  const [progressMessages, setProgressMessages] = useState<ScrapeEventMessage[]>([]);
  const [isProgressComplete, setIsProgressComplete] = useState(false);
  const [isProgressError, setIsProgressError] = useState(false);

  const showBulkFeedback = (text: string, ok: boolean) => {
    setBulkMessage({ text, ok });
    setTimeout(() => setBulkMessage(null), 5000);
  };

  const handleScrapeAllMissing = async () => {
    lightTap();
    setIsScrapingMissing(true);
    setProgressTitle('Scraping Missing Data');
    setProgressMessages([]);
    setIsProgressComplete(false);
    setIsProgressError(false);
    setIsProgressModalOpen(true);
    try {
      await linkedinService.scrapeAllMissing((msg) => {
        setProgressMessages((prev) => [...prev, msg]);
      });
      posthog.capture('linkedin_bulk_scrape_completed', { scrape_scope: 'missing_profiles' });
      setIsProgressComplete(true);
    } catch (err) {
      setIsProgressError(true);
      showBulkFeedback(`Error: ${err instanceof Error ? err.message : 'Scrape failed'}`, false);
    } finally {
      setIsScrapingMissing(false);
    }
  };

  const handleScrapeAll = async () => {
    lightTap();
    if (!window.confirm('Re-scrape ALL profiles with a LinkedIn URL? This will overwrite existing LinkedIn data and may take a while.')) return;
    setIsScrapingAll(true);
    setProgressTitle('Re-scraping All Profiles');
    setProgressMessages([]);
    setIsProgressComplete(false);
    setIsProgressError(false);
    setIsProgressModalOpen(true);
    try {
      await linkedinService.scrapeAllProfiles((msg) => {
        setProgressMessages((prev) => [...prev, msg]);
      });
      posthog.capture('linkedin_bulk_scrape_completed', { scrape_scope: 'all_profiles' });
      setIsProgressComplete(true);
    } catch (err) {
      setIsProgressError(true);
      showBulkFeedback(`Error: ${err instanceof Error ? err.message : 'Scrape failed'}`, false);
    } finally {
      setIsScrapingAll(false);
    }
  };

  const {
    searchTerm,
    batchFilter,
    setBatchFilter,
    departmentFilter,
    setDepartmentFilter,
    locationFilter,
    setLocationFilter,
    industryFilter,
    setIndustryFilter,
    linkedinStatusFilter,
    setLinkedinStatusFilter,
    researchLockedFilter,
    setResearchLockedFilter,
    alumniList,
    loading,
    error,
    page,
    setPage,
    totalPages,
    totalItems,
    sortBy,
    sortOrder,
    handleSearchChange,
    handleSort,
    refresh,
  } = useAlumni(limit);

  useScrollRestoration(!loading && alumniList.length > 0);

  const navigate = useNavigate();

  // In the URL, so coming back from a profile returns to the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'changes' ? 'changes' : 'profiles';
  const setView = (next: 'profiles' | 'changes') =>
    setSearchParams(next === 'changes' ? { view: 'changes' } : {}, { replace: true });

  const handleAlumniClick = (alumni: AlumniProfile) => {
    mediumTap();
    navigate(`/alumni/${alumni.donor_id || alumni._id}`);
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 1 ? (
      <CaretUpIcon size={11} weight="bold" className="shrink-0" />
    ) : (
      <CaretDownIcon size={11} weight="bold" className="shrink-0" />
    );
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6 font-sans">
        {/* Header */}
        <PageHeader
          breadcrumb={[
            { label: 'Dashboard', onClick: onBack },
            { label: 'Alumni Directory' },
          ]}
          title="Alumni Directory"
          subtitle="Connect with verified graduates and trace their corporate footprints."
          actions={
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Admin-only LinkedIn bulk actions */}
              {isAdmin && (
                <>
                  {/* Feedback toast */}
                  {bulkMessage && (
                    <span
                      className={`text-xs font-semibold px-3 py-1.5 rounded-xl border ${
                        bulkMessage.ok
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-rose-50 text-rose-600 border-rose-200'
                      }`}
                    >
                      {bulkMessage.text}
                    </span>
                  )}

                  {/* Scraper Settings */}
                  <Button
                    onClick={() => setIsScraperSettingsOpen(true)}
                    variant="outline"
                    className="flex items-center justify-center p-2 text-slate-500 shadow-sm"
                    title="Scraper Settings"
                  >
                    <GearIcon size={16} weight="bold" />
                  </Button>

                  {/* Scrape Missing */}
                  <Button
                    onClick={handleScrapeAllMissing}
                    disabled={isScrapingMissing || isScrapingAll}
                    variant="outline"
                    className="flex items-center gap-1.5 text-xs shadow-sm"
                    title="Scrape LinkedIn for profiles with no existing data"
                  >
                    {isScrapingMissing ? (
                      <Spinner size={13} className="animate-spin" />
                    ) : (
                      <svg className="w-3 h-3 fill-current text-[#0A66C2]" viewBox="0 0 24 24" aria-hidden>
                        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                      </svg>
                    )}
                    {isScrapingMissing ? 'Queueing…' : 'Scrape Missing Linkedin'}
                  </Button>

                  {/* Update All */}
                  <Button
                    onClick={handleScrapeAll}
                    disabled={isScrapingAll || isScrapingMissing}
                    variant="outline"
                    className="flex items-center gap-1.5 text-xs shadow-sm border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300"
                    title="Re-scrape ALL profiles — overwrites existing data"
                  >
                    {isScrapingAll ? (
                      <Spinner size={13} className="animate-spin" />
                    ) : (
                      <svg className="w-3 h-3 fill-current text-amber-600" viewBox="0 0 24 24" aria-hidden>
                        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                      </svg>
                    )}
                    {isScrapingAll ? 'Queueing…' : 'Update All LinkedIn'}
                  </Button>
                </>
              )}

              {/* Admin-only LinkedIn Overrides Export */}
              {isAdmin && (
                <Button
                  onClick={async () => {
                    try {
                      const blob = await alumniService.exportLinkedinOverrides();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'linkedin_overrides.csv';
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (err: unknown) {
                      alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
                    }
                  }}
                  variant="outline"
                  className="flex items-center gap-1.5 shadow-sm border-slate-200"
                  title="Download CSV of profiles with manually overwritten LinkedIn URLs"
                >
                  <FilePdf size={14} className="text-slate-500" />
                  Export Overrides
                </Button>
              )}

              {/* Admin-only Import Data */}
              {isAdmin && (
                <Button
                  onClick={() => {
                    lightTap();
                    setIsImportModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 shadow-sm"
                >
                  <UploadSimpleIcon size={14} weight="bold" />
                  Import Data
                </Button>
              )}

              {/* Add Data (for all) */}
              <Button
                onClick={() => {
                  lightTap();
                  setIsAddDataModalOpen(true);
                }}
                className="flex items-center gap-1.5 shadow-sm bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 256 256">
                  <path d="M228,128a100,100,0,1,1-100-100A100.11,100.11,0,0,1,228,128Zm-24,0a76,76,0,1,0-76,76A76.08,76.08,0,0,0,204,128Zm-36-12H140V84a12,12,0,0,0-24,0v32H88a12,12,0,0,0,0,24h28v32a12,12,0,0,0,24,0V140h28a12,12,0,0,0,0-24Z"></path>
                </svg>
                Add Data
              </Button>
            </div>
          }
        />

        {/* Card: Search + Table + Pagination */}
        <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col relative z-10">

          {/* View: the profiles themselves, or what AI research reruns changed */}
          <div className="flex items-center gap-1 px-5 pt-4">
            {([['profiles', 'All profiles'], ['changes', 'Research changes']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { lightTap(); setView(key); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  view === key ? 'bg-accent/10 text-accent' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {view === 'changes' ? (
            <ResearchChangesTable />
          ) : (
          <>
          {/* Toolbar */}
          <div className="flex flex-col gap-4 p-5 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative w-full sm:max-w-md flex items-center gap-2">
                <div className="relative w-full">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <MagnifyingGlassIcon size={15} weight="bold" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name, company, role..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-medium"
                  />
                </div>
                <button
                  onClick={() => {
                    lightTap();
                    setShowFilters(!showFilters);
                  }}
                  className={`p-2.5 rounded-xl border transition-colors shrink-0 ${showFilters ? 'bg-accent/10 border-accent/20 text-accent' : 'bg-slate-50 border-slate-200/60 text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
                  title="Toggle Filters"
                >
                  <FadersIcon size={18} weight={showFilters ? "fill" : "regular"} />
                </button>

              </div>
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider whitespace-nowrap">
                Total Profiles: <span className="text-slate-800 font-extrabold">{totalItems}</span>
              </div>
            </div>
            
            <AnimatePresence>
              {showFilters && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1, marginTop: 4 }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col sm:flex-row gap-3 overflow-hidden"
                >
                  <input
                    type="text"
                    placeholder="Batch"
                    value={batchFilter}
                    onChange={(e) => { setBatchFilter(e.target.value); setPage(1); }}
                    className="w-full sm:w-32 bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-medium"
                  />
                  <input
                    type="text"
                    placeholder="Department (e.g. Computer Science)"
                    value={departmentFilter}
                    onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }}
                    className="w-full sm:w-48 bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-medium"
                  />
                  <input
                    type="text"
                    placeholder="Location (e.g. Chennai)"
                    value={locationFilter}
                    onChange={(e) => { setLocationFilter(e.target.value); setPage(1); }}
                    className="w-full sm:w-40 bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-medium"
                  />
                  <select
                    value={industryFilter}
                    onChange={(e) => { setIndustryFilter(e.target.value); setPage(1); }}
                    className="w-full sm:w-48 bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 transition-smooth font-medium"
                  >
                    <option value="">-- All Sectors --</option>
                    {BUSINESS_SECTORS.map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                  <select
                    value={linkedinStatusFilter || ''}
                    onChange={(e) => { setLinkedinStatusFilter(e.target.value); setPage(1); }}
                    className="w-full sm:w-48 bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 transition-smooth font-medium"
                  >
                    <option value="">LinkedIn Status: All</option>
                    <option value="manual_override">Has Manual Override</option>
                    <option value="overwritten">Overwritten by Import</option>
                    <option value="no_linkedin">No LinkedIn</option>
                  </select>
                  <select
                    value={researchLockedFilter || ''}
                    onChange={(e) => { setResearchLockedFilter(e.target.value); setPage(1); }}
                    className="w-full sm:w-48 bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 transition-smooth font-medium"
                  >
                    <option value="">Research Lock: All</option>
                    <option value="true">Locked</option>
                    <option value="false">Unlocked</option>
                  </select>
                </motion.div>
              )}
            </AnimatePresence>


          </div>

          {/* Table */}
          <div className="overflow-auto shrink-0" style={{ height: '680px' }}>
            <table className="w-full border-collapse text-left">
              <thead className="sticky top-0 bg-white z-20 shadow-sm">
                <tr className="border-b border-slate-100 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                  {[
                    { label: 'Alumni Name', field: 'name', className: 'pb-3 pl-5 w-[18%]' },
                    { label: 'Batch', field: 'batch', className: 'pb-3 w-[8%]' },
                    { label: 'Department', field: 'department', className: 'pb-3 w-[12%]' },
                    { label: 'Location', field: 'location', className: 'pb-3 w-[12%]' },
                    { label: 'Cumulative Giving', field: 'cumulative_giving', className: 'pb-3 text-right w-[13%]' },
                    { label: 'Target Ask (VALS)', field: 'target_ask', className: 'pb-3 text-right w-[13%]' },
                    { label: 'Net Worth', field: 'net_worth', className: 'pb-3 text-right w-[13%]' },
                    { label: 'Modified', field: 'last_modified', className: 'pb-3 pr-5 text-right w-[11%]' },
                  ].map(({ label, field, className }) => (
                    <th
                      key={field}
                      className={`${className} cursor-pointer hover:text-slate-700 transition-colors`}
                      onClick={() => handleSort(field)}
                    >
                      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
                        {label} {renderSortIcon(field)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 text-xs font-sans">
                {loading ? (
                  <TableRowSkeleton cols={8} rows={10} />
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-rose-500 font-semibold">{error}</td>
                  </tr>
                ) : alumniList.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-slate-400 font-medium">No matching alumni found.</td>
                  </tr>
                ) : (
                  alumniList.map((alumni) => {
                    const sInfo = alumni.source_info ?? ({} as NonNullable<typeof alumni.source_info>);
                    const lInfo = alumni.linkedin_info ?? ({} as NonNullable<typeof alumni.linkedin_info>);
                    const lProfile = alumni.linkedin_profile ?? ({} as NonNullable<typeof alumni.linkedin_profile>);

                    const rawName = sInfo.current_name || sInfo.csv_name || lInfo.name || lProfile.name;
                    const name = typeof rawName === 'string' && rawName.trim() !== '' ? rawName.trim() : safeStr(rawName, 'Name not found');

                    const initials = name.split(' ').filter(Boolean).slice(0, 2).map((n: string) => n[0] ?? '').join('').toUpperCase() || 'A';
                    const imageUrl = lInfo.avatar || lInfo.photo || lInfo.profilePic;

                    // Parse cumulative giving and recommended ask for comparison
                    const cumulativeGiving = parseToNumber(
                      safeStr(sInfo.cumulative_giving_to_iitm || "0")
                    ) ?? 0;

                    const recommendedAsk = parseToNumber(
                      safeStr(
                        (alumni.vals_framework as any)?.estimated_financial_capacity
                          ?.recommended_ask_inr ||
                        alumni.ai_research?.structured_data?.targeted_donation_amount ||
                        ""
                        )
                      );

                    const cumulativeGivingColor =
                      recommendedAsk === null
                        ? "text-slate-900"
                        : cumulativeGiving >= recommendedAsk
                          ? "text-emerald-600"
                          : "text-rose-600";
                          
                    const isAlumni = alumni.is_alumni !== false;

                    return (
                      <tr
                        key={alumni._id}
                        onClick={() => handleAlumniClick(alumni)}
                        className="hover:bg-slate-50/80 transition-smooth group cursor-pointer"
                      >
                        {/* 1. Alumni Name */}
                        <td className="py-4 pl-5">
                          <div className="flex items-center gap-3">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={name}
                                className="w-8 h-8 rounded-full object-cover shadow-xs shrink-0"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
                                }}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-accent-hover text-white text-[10px] font-black flex items-center justify-center shadow-xs shrink-0 relative">
                                {initials}
                                {alumni.ai_research?.structured_data && (
                                  <div className="h-2.5 w-2.5 bg-emerald-400 rounded-full absolute -top-0.5 -right-0.5 ring-2 ring-white">
                                  </div>
                                )}
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-slate-800 group-hover:text-accent transition-colors">{name}</p>
                                {alumni.research_locked && (
                                  <span title="Research locked">
                                    <Lock size={12} weight="fill" className="text-amber-500 shrink-0" />
                                  </span>
                                )}
                                {!isAlumni && (
                                  <span className="bg-rose-100 text-rose-700 border border-rose-200 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                                    Non-Alumni
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        {/* 2. Batch */}
                        <td className="py-4">
                          <p className="font-bold text-slate-700">{safeStr(sInfo.batch)}</p>
                        </td>
                        
                        {/* 3. Department */}
                        <td className="py-4">
                          <p className="font-semibold text-slate-700 leading-snug truncate max-w-[150px] hover:max-w-full transition-all duration-100">
                            {safeStr(sInfo.department)}
                          </p>
                        </td>

                        {/* Location */}
                        <td className="py-4">
                          <p className="font-medium text-slate-600 leading-snug truncate max-w-[120px] hover:max-w-full transition-all duration-100">
                            {safeStr(sInfo.work_city || sInfo.person_account_work_address_country_territory || lInfo.location || lInfo.city, 'N/A')}
                          </p>
                        </td>
                        
                        {/* 4. Cumulative Giving */}
                        <td className="py-4 text-right">
                          <span className={`font-extrabold ${cumulativeGivingColor}`}>
                            {formatToCr(safeStr(sInfo.cumulative_giving_to_iitm || "0"))}
                          </span>
                        </td>
                        
                        {/* 5. Target Ask (VALS) */}
                        <td className="py-4 text-right">
                          <span className="font-bold text-emerald-600">
                            {formatDirectoryToCr(safeStr((alumni.vals_framework as any)?.estimated_financial_capacity?.recommended_ask_inr || alumni.ai_research?.structured_data?.targeted_donation_amount || 'N/A'))}
                          </span>
                        </td>
                        
                        {/* 6. Net Worth */}
                        <td className="py-4 text-right">
                          <span className="font-bold text-slate-700">
                            {formatDirectoryToCr(safeStr((alumni.vals_framework as any)?.estimated_financial_capacity?.net_worth_inr || 'N/A'))}
                          </span>
                        </td>
                        
                        {/* 7. Modified — hovering the date shows what changed and who changed it */}
                        <td className="py-4 pr-5 text-right text-slate-500 font-medium">
                          <span
                            title={[
                              alumni.last_modified_source && `Change: ${MODIFIED_SOURCE_LABELS[alumni.last_modified_source] ?? alumni.last_modified_source}`,
                              alumni.last_modified_by && `By: ${alumni.last_modified_by}`,
                            ].filter(Boolean).join('\n') || undefined}
                          >
                            {alumni.last_modified_date
                              ? new Date(alumni.last_modified_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && !error && alumniList.length > 0 && (
            <div className="px-5 pb-5">
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                itemLabel="alumni profile"
                onPageChange={(p) => setPage(p)}
              />
            </div>
          )}
          </>
          )}
        </div>

        {/* Import Modal */}
        <ImportDataModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={() => { setPage(1); refresh(); }}
        />
        
        {/* Add Data Modal */}
        <AddDataModal
          isOpen={isAddDataModalOpen}
          onClose={() => setIsAddDataModalOpen(false)}
          onSuccess={() => { setPage(1); refresh(); }}
        />
        
        {/* Scraper Settings Modal */}
        <ScraperSettingsModal
          isOpen={isScraperSettingsOpen}
          onClose={() => setIsScraperSettingsOpen(false)}
        />

        {/* Scrape Progress Modal */}
        <ScrapeProgressModal
          isOpen={isProgressModalOpen}
          onClose={() => setIsProgressModalOpen(false)}
          title={progressTitle}
          messages={progressMessages}
          isComplete={isProgressComplete}
          isError={isProgressError}
        />
      </div>
    </ErrorBoundary>
  );
}
